<template>
  <div class="shipping-modern">
    <div class="shipping-card">
      <h3 class="shipping-title">{{ $t('shipping.title') }}</h3>
      <AlterarEnderecoModal v-if="showAddressModal" @close="showAddressModal = false" @salvar="handleEnderecoCriado" />


      <!-- Loading state -->
      <div v-if="loading" class="shipping-loading">
        <div class="shipping-spinner">
          <div class="spinner"></div>
          <span>{{ $t('shipping.loading') }}</span>
        </div>
      </div>

      <div class="shipping-content" :class="{ 'is-loading': loading }">
        <!-- Botão para exibir novamente as opções de entrega -->
        <button v-if="!showDeliveryOptions" @click="showDeliveryOptions = true" class="show-delivery-btn">
          {{ $t('shipping.show_delivery_options') }}
        </button>

        <!-- Lista de endereços para seleção -->
        <div v-if="user?.enderecos?.length && showDeliveryOptions" class="shipping-address-selection">
          <h4 class="shipping-subtitle">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M17.6569 16.6569C16.7202 17.5935 14.7616 19.5521 13.4138 20.8999C12.6327 21.681 11.3677 21.6814 10.5866 20.9003C9.26234 19.576 7.34159 17.6553 6.34315 16.6569C3.21895 13.5327 3.21895 8.46734 6.34315 5.34315C9.46734 2.21895 14.5327 2.21895 17.6569 5.34315C20.781 8.46734 20.781 13.5327 17.6569 16.6569Z"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              <path
                d="M15 11C15 12.6569 13.6569 14 12 14C10.3431 14 9 12.6569 9 11C9 9.34315 10.3431 8 12 8C13.6569 8 15 9.34315 15 11Z"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span>{{ $t('checkout.step1.select_address') }}</span>
          </h4>

          <div class="address-list">
            <div v-for="endereco in user.enderecos" :key="endereco.id"
              :class="['address-item', { selected: endereco.id === clienteEnderecoId }]"
              @click="selecionarEndereco(endereco)">
              <input type="radio" :id="'endereco-' + endereco.id" name="cliente_endereco"
                :checked="endereco.id === clienteEnderecoId" class="radio-hidden" />
              <label :for="'endereco-' + endereco.id" class="address-content">
                <strong>{{ endereco.nome || $t('address.no_name') }}</strong>
                <small>
                  {{ endereco.logradouro }}, {{ endereco.numero }} -
                  {{ endereco.bairro }},
                  {{ endereco.cidade }} / {{ endereco.estado }}
                </small>
                <small>{{ endereco.cep }} - {{ endereco.pais }}</small>
                <span v-if="endereco.principal" class="badge-main">
                  {{ $t('address.main_address') }}
                </span>
              </label>
            </div>
            <button class="btn-primary" @click="showAddressModal = true">
              {{ $t('address.add_new') }}
            </button>

          </div>

        </div>

        <div v-if="!showDeliveryOptions">
          <div v-if="currentLocationHeader && !showDeliveryOptions" class="shipping-location-header">
            <div class="address-badge" :class="{ 'pickup-point': currentLocationHeader.type === 'pickup_point' }">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12 12.5C13.1046 12.5 14 11.6046 14 10.5C14 9.39543 13.1046 8.5 12 8.5C10.8954 8.5 10 9.39543 10 10.5C10 11.6046 10.8954 12.5 12 12.5Z"
                  fill="currentColor" />
                <path
                  d="M12 22C10 18 4 14.4183 4 10C4 5.58172 7.58172 2 12 2C16.4183 2 20 5.58172 20 10C20 14.4183 14 18 12 22Z"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <span v-if="currentLocationHeader.type === 'user_address'">
                {{ $t('shipping.using_user_address') }}:
                <strong>{{ currentLocationHeader.address }}</strong>
              </span>
              <span v-else>
                {{ $t('shipping.using_pickup_point') }}:
                <strong>{{ currentLocationHeader.name }}</strong>
                <template v-if="currentLocationHeader.address">
                  ({{ currentLocationHeader.address }})
                </template>
              </span>
            </div>
          </div>

          <!-- Delivery unavailable notice -->
          <div v-if="onlyPickupAvailable" class="shipping-notice">
            <div class="notice-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12 9V11M12 15H12.01M5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21Z"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
            <p>{{ $t('shipping.no_delivery_available') }}</p>
          </div>

          <!-- Delivery options -->
          <div v-if="deliveryOptions.length > 0" class="shipping-options">
            <h4 class="shipping-subtitle">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 18H5M8 12H5M13 18H11M17 14V6M14 18H19M15 8H19M3 8H11M3 12H7M3 16H7" stroke="currentColor"
                  stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <span>{{ $t('shipping.delivery_method') }}</span>
            </h4>

            <div class="shipping-options-list">
              <div v-for="option in deliveryOptions" :key="option.entrega_servico_id || 'retirada_loja'"
                class="shipping-option" :class="{ 'is-selected': isOptionSelected(option) }"
                @click="selectDeliveryOption(option)">

                <div class="shipping-option-radio">
                  <input type="radio" :id="'delivery-' + (option.entrega_servico_id || 'retirada_loja')"
                    v-model="currentSelection.optionId" :value="option.entrega_servico_id || 'retirada_loja'"
                    class="shipping-radio-input" />
                  <div class="shipping-radio-control"></div>
                </div>

                <label :for="'delivery-' + (option.entrega_servico_id || 'retirada_loja')"
                  class="shipping-option-label">
                  <div class="shipping-option-content">
                    <div class="option-details">
                      <span class="shipping-option-name">
                        {{ option.descricao || $t('shipping.pickup_in_store') }}
                      </span>
                      <span v-if="option.prazo" class="shipping-option-time">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                        {{ option.prazo }} {{ $t('shipping.days') }}
                      </span>
                    </div>
                    <span v-if="option.valor > 0" class="shipping-option-price">
                      {{ formatCurrency(option.valor, option.moeda) }}
                    </span>
                    <span v-if="option.valor === 0" class="shipping-option-free">
                      {{ $t('shipping.free_shipping') }}
                    </span>
                  </div>

                  <!-- Pickup points sub-options -->
                  <div v-if="option.opcionais?.pontos_entrega && isOptionSelected(option)" class="shipping-sub-options">
                    <!-- Caso seja um array de pontos -->
                    <template v-if="Array.isArray(option.opcionais.pontos_entrega)">
                      <div v-for="ponto in option.opcionais.pontos_entrega" :key="ponto?.id" class="shipping-sub-option"
                        :class="{ 'is-selected': isPickupPointSelected(option, ponto) }"
                        @click.stop="selectPickupPointFromOption(option, ponto)">

                        <div class="shipping-sub-option-radio">
                          <input type="radio" :id="'ponto-' + (ponto?.id || 'unique')"
                            :name="'pontos-' + (option.entrega_servico_id || 'retirada_loja')"
                            class="shipping-radio-input" :checked="isPickupPointSelected(option, ponto)" />
                          <div class="shipping-radio-control"></div>
                        </div>

                        <label :for="'ponto-' + (ponto?.id || 'unique')" class="shipping-sub-option-label">
                          <div class="shipping-sub-option-content">
                            <span class="shipping-sub-option-name">{{ ponto?.punto_entrega || ponto?.nome }}</span>
                            <span class="shipping-sub-option-address">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <path
                                  d="M17.6569 16.6569C16.7202 17.5935 14.7616 19.5521 13.4138 20.8999C12.6327 21.681 11.3677 21.6814 10.5866 20.9003C9.26234 19.576 7.34159 17.6553 6.34315 16.6569C3.21895 13.5327 3.21895 8.46734 6.34315 5.34315C9.46734 2.21895 14.5327 2.21895 17.6569 5.34315C20.781 8.46734 20.781 13.5327 17.6569 16.6569Z"
                                  stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                  stroke-linejoin="round" />
                                <path
                                  d="M15 11C15 12.6569 13.6569 14 12 14C10.3431 14 9 12.6569 9 11C9 9.34315 10.3431 8 12 8C13.6569 8 15 9.34315 15 11Z"
                                  stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                  stroke-linejoin="round" />
                              </svg>
                              {{ ponto?.direccion || ponto?.endereco }}
                            </span>
                            <span v-if="ponto?.horario_atencion || ponto?.horario_atendimento"
                              class="shipping-sub-option-hours">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <path
                                  d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                                  stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                  stroke-linejoin="round" />
                              </svg>
                              {{ $t('shipping.opening_hours') }}: {{ ponto?.horario_atencion ||
                                ponto?.horario_atendimento
                              }}
                            </span>
                          </div>
                        </label>
                      </div>
                    </template>

                    <!-- Caso seja um único ponto (objeto) -->
                    <template v-else>
                      <div class="shipping-sub-option"
                        :class="{ 'is-selected': isPickupPointSelected(option, option.opcionais.pontos_entrega) }"
                        @click.stop="selectPickupPointFromOption(option, option.opcionais.pontos_entrega)">

                        <div class="shipping-sub-option-radio">
                          <input type="radio" :id="'ponto-' + (option.opcionais.pontos_entrega?.id || 'unique')"
                            :name="'pontos-' + (option.entrega_servico_id || 'retirada_loja')"
                            class="shipping-radio-input"
                            :checked="isPickupPointSelected(option, option.opcionais.pontos_entrega)" />
                          <div class="shipping-radio-control"></div>
                        </div>

                        <label :for="'ponto-' + (option.opcionais.pontos_entrega?.id || 'unique')"
                          class="shipping-sub-option-label">
                          <div class="shipping-sub-option-content">
                            <span class="shipping-sub-option-name">{{ option.opcionais.pontos_entrega?.punto_entrega
                            }}</span>
                            <span class="shipping-sub-option-address">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <path
                                  d="M17.6569 16.6569C16.7202 17.5935 14.7616 19.5521 13.4138 20.8999C12.6327 21.681 11.3677 21.6814 10.5866 20.9003C9.26234 19.576 7.34159 17.6553 6.34315 16.6569C3.21895 13.5327 3.21895 8.46734 6.34315 5.34315C9.46734 2.21895 14.5327 2.21895 17.6569 5.34315C20.781 8.46734 20.781 13.5327 17.6569 16.6569Z"
                                  stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                  stroke-linejoin="round" />
                                <path
                                  d="M15 11C15 12.6569 13.6569 14 12 14C10.3431 14 9 12.6569 9 11C9 9.34315 10.3431 8 12 8C13.6569 8 15 9.34315 15 11Z"
                                  stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                  stroke-linejoin="round" />
                              </svg>
                              {{ option.opcionais.pontos_entrega?.direccion }}
                            </span>
                            <span v-if="option.opcionais.pontos_entrega?.horario_atencion"
                              class="shipping-sub-option-hours">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <path
                                  d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                                  stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                  stroke-linejoin="round" />
                              </svg>
                              {{ $t('shipping.opening_hours') }}: {{ option.opcionais.pontos_entrega?.horario_atencion
                              }}
                            </span>
                          </div>
                        </label>
                      </div>
                    </template>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <!-- Pickup points section -->
          <div v-if="!user.estrangeiro" class="shipping-pickup">
            <!-- <div class="shipping-pickup-option" :class="{ 'is-selected': selectedDeliveryType === 'pickup_point' }"
              @click="switchToPickupPoints">

              <div class="shipping-option-radio">
                <input type="radio" id="pickup-points-option" v-model="selectedDeliveryType" value="pickup_point"
                  class="shipping-radio-input" />
                <div class="shipping-radio-control"></div>
              </div>

              <label for="pickup-points-option" class="shipping-option-label">
                <div class="shipping-option-content">
                  <div class="option-details">
                    <span class="shipping-option-name">
                      {{ $t('shipping.pickup_points_option') }}
                    </span>
                  </div>
                </div>
              </label>
            </div> -->

            <!-- Hierarchical selection for pickup points -->
            <div v-if="selectedDeliveryType === 'pickup_point'" class="shipping-pickup-points">
              <div class="shipping-grid">
                <!-- Estado -->
                <div class="shipping-field">
                  <label class="shipping-label">{{ $t('shipping.state') }}</label>
                  <div class="select-wrapper">
                    <select v-model="selectedPickupState" @change="updatePickupCities" class="shipping-select">
                      <option value="">{{ $t('shipping.choose_state') }}</option>
                      <option v-for="state in pickupStates" :key="state" :value="state">
                        {{ state }}
                      </option>
                    </select>
                    <div class="select-arrow"></div>
                  </div>
                </div>

                <!-- Cidade -->
                <div v-if="selectedPickupState" class="shipping-field">
                  <label class="shipping-label">{{ $t('shipping.city') }}</label>
                  <div class="select-wrapper">
                    <select v-model="selectedPickupCity" @change="updatePickupPoints" class="shipping-select">
                      <option value="">{{ $t('shipping.choose_city') }}</option>
                      <option v-for="city in pickupCities" :key="city" :value="city">
                        {{ city }}
                      </option>
                    </select>
                    <div class="select-arrow"></div>
                  </div>
                </div>
              </div>

              <!-- Lista de Pontos de Entrega -->
              <div v-if="filteredPickupPoints.length > 0" class="shipping-pickup-points-list">
                <div v-for="point in filteredPickupPoints" :key="point.id" class="shipping-pickup-point"
                  :class="{ 'is-selected': currentSelection.pickupPointId === point.id }"
                  @click="selectPickupPoint(point)">

                  <div class="shipping-pickup-point-radio">
                    <input type="radio" :id="'pickup-' + point.id" name="pickup-points" class="shipping-radio-input"
                      :checked="currentSelection.pickupPointId === point.id" />
                    <div class="shipping-radio-control"></div>
                  </div>

                  <label :for="'pickup-' + point.id" class="shipping-pickup-point-label">
                    <div class="shipping-pickup-point-content">
                      <span class="shipping-pickup-point-name">{{ point.ponto_entrega }}</span>
                      <span class="shipping-pickup-point-address">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M17.6569 16.6569C16.7202 17.5935 14.7616 19.5521 13.4138 20.8999C12.6327 21.681 11.3677 21.6814 10.5866 20.9003C9.26234 19.576 7.34159 17.6553 6.34315 16.6569C3.21895 13.5327 3.21895 8.46734 6.34315 5.34315C9.46734 2.21895 14.5327 2.21895 17.6569 5.34315C20.781 8.46734 20.781 13.5327 17.6569 16.6569Z"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                          <path
                            d="M15 11C15 12.6569 13.6569 14 12 14C10.3431 14 9 12.6569 9 11C9 9.34315 10.3431 8 12 8C13.6569 8 15 9.34315 15 11Z"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                        {{ point.ponto_entrega }} {{ point.endereco }}
                      </span>
                      <span v-if="point.horario_atendimento" class="shipping-pickup-point-hours">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                        {{ $t('shipping.opening_hours') }}: {{ point.horario_atendimento }}
                      </span>
                    </div>
                  </label>
                </div>
              </div>

              <div v-if="selectedPickupCity && filteredPickupPoints.length === 0" class="shipping-no-results">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 9V11M12 15H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <p>{{ $t('shipping.no_pickup_points') }}</p>
              </div>
            </div>
          </div>

          <p v-if="errorMessage" class="shipping-error">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 9V11M12 15H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            {{ errorMessage }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { calculateShipping, fetchPickupPoints } from "@/services/apiService";
import { estados_py, estados_br } from "./Cidades";
import { mapGetters, mapState, mapActions } from "vuex";
import AlterarEnderecoModal from "../Usuario/AlterarEnderecoModal.vue";

export default {
  components: {
    AlterarEnderecoModal,
  },
  props: {
    cart: Object,
  },
  data() {
    return {
      errorMessage: "",
      estados: [],
      cities: [],
      loading: false,
      loadingPickupPoints: false,
      onlyPickupAvailable: false,
      selectedDeliveryType: null,
      selectedOptionBeforeLoading: null,
      currentSelection: {
        optionId: null,
        pickupPointId: null
      },
      selectedPickupState: '',
      selectedPickupCity: '',
      pickupStates: [],
      pickupCities: [],
      filteredPickupPoints: [],
      allPickupPoints: [],
      clienteEnderecoId: null,
      showDeliveryOptions: true,
      showAddressModal: false,
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/getUser",
    }),
    ...mapState("shipping", [
      "selectedState",
      "selectedCity",
      "deliveryOptions",
      "shippingCost",
    ]),
    selectedStateName() {
      return this.estados.find(e => e.estado_id === parseInt(this.selectedState, 10))?.estado_nome || "";
    },
    selectedCityName() {
      return this.cities.find(c => c.cidade_id === parseInt(this.selectedCity, 10))?.nome_cidade || "";
    },
    isOnlyPickupOption() {
      return this.deliveryOptions.length === 1 &&
        this.deliveryOptions[0].entrega_servico_id === null;
    },
    currentLocationHeader() {
      if (this.currentSelection.pickupPointId) {
        const selectedPoint = this.findSelectedPickupPoint();

        if (selectedPoint) {
          return {
            type: 'pickup_point',
            name: selectedPoint.punto_entrega || selectedPoint.nome || 'Ponto de Retirada',
            address: selectedPoint.endereco
          };
        }

        if (this.selectedOptionBeforeLoading?.type === 'pickup_point') {
          return {
            type: 'pickup_point',
            name: this.selectedOptionBeforeLoading.name || 'Ponto de Retirada',
            address: this.selectedOptionBeforeLoading.point?.endereco || ''
          };
        }
      }

      if (this.clienteEnderecoId && this.user?.enderecos?.length > 0) {
        const enderecoSelecionado = this.user.enderecos.find(e => e.id === this.clienteEnderecoId);
        if (enderecoSelecionado) {
          return {
            type: 'user_address',
            address: `${enderecoSelecionado.logradouro}, ${enderecoSelecionado.cidade}, ${enderecoSelecionado.estado}`
          };
        }
      }

      return null;
    }

  },
  methods: {
    ...mapActions("shipping", [
      "updateSelectedState",
      "updateSelectedCity",
      "updateDeliveryOptions",
      "updateSelectedDeliveryOption",
      "updateShippingCost",
    ]),
    handleNovoEndereco(novoEndereco) {
      this.user.enderecos.push(novoEndereco);
      this.clienteEnderecoId = novoEndereco.id;
      this.showAddressModal = false;

      this.selecionarEndereco(novoEndereco);
    },
    async handleEnderecoCriado() {
      this.showAddressModal = false;

      try {
        await this.$store.dispatch('auth/fetchUser');

        this.autoFillUserLocation();
      } catch (err) {
        console.error("Erro ao atualizar dados do usuário:", err);
      }
    },
    updateCities() {
      const selectedEstado = this.estados.find(estado => estado.estado_id === parseInt(this.selectedState, 10));
      this.cities = selectedEstado ? selectedEstado.cidades : [];
      this.updateSelectedCity("");
      this.updateDeliveryOptions([]);
      this.pickupPoints = [];
      this.onlyPickupAvailable = false;
      this.selectedDeliveryType = null;
      this.currentSelection = {
        optionId: null,
        pickupPointId: null
      };
    },
    selecionarEndereco(endereco) {
      this.clienteEnderecoId = endereco.id;
      this.selectedState = endereco.estado_id || "";
      this.selectedCity = endereco.cidade_id || "";

      this.estados = endereco.pais_id === 1 ? estados_br : estados_py;
      const estado = this.estados.find(e => e.estado_id === endereco.estado_id);
      this.cities = estado ? estado.cidades : [];

      this.showDeliveryOptions = false; // Oculta as opções
      this.calculateShipping({ cidade_id: endereco.cidade_id });
    },

    findSelectedPickupPoint() {
      // Procurar nos pontos de entrega das opções de frete
      for (const option of this.deliveryOptions) {
        if (option.opcionais?.pontos_entrega) {
          const points = Array.isArray(option.opcionais.pontos_entrega) ?
            option.opcionais.pontos_entrega :
            [option.opcionais.pontos_entrega];

          const found = points.find(p => p.id === this.currentSelection.pickupPointId);
          if (found) return found;
        }
      }

      // Procurar na lista de pontos de retirada
      if (this.allPickupPoints.length > 0) {
        return this.allPickupPoints.find(p => p.id === this.currentSelection.pickupPointId);
      }

      return null;
    },
    async calculateShipping(params = {}) {
      this.clearShippingState();

      this.loading = true;
      this.onlyPickupAvailable = false;
      this.selectedDeliveryType = null;
      this.currentSelection = {
        optionId: null,
        pickupPointId: null
      };

      try {
        const { shippingCost, deliveryOptions } = await calculateShipping(
          this.cart,
          params.cidade_id || this.selectedCity,
          this.selectedStateName,
          this.user,
          null,
          {
            cliente_endereco_id: this.clienteEnderecoId
          }
        );

        this.updateDeliveryOptions(deliveryOptions);
        this.updateShippingCost(shippingCost);

        if (this.isOnlyPickupOption) {
          this.onlyPickupAvailable = true;
          await this.fetchPickupPoints();
        }

        // 🔽 Se houver apenas uma opção de frete, seleciona automaticamente
        if (deliveryOptions.length === 1) {
          const unicaOpcao = deliveryOptions[0];
          this.selectDeliveryOption(unicaOpcao);
        }

        this.saveShippingData();
      } catch (error) {
        this.errorMessage = this.$t('shipping.api_error');
        console.error("Erro ao calcular frete:", error);
      } finally {
        this.loading = false;
      }
    },

    clearShippingState() {
      this.errorMessage = "";
      this.updateDeliveryOptions([]);
      this.updateShippingCost(0);
      this.onlyPickupAvailable = false;
      this.filteredPickupPoints = [];
    },


    // Modified to process pickup points data
    async fetchPickupPoints() {
      this.loadingPickupPoints = true;
      try {
        const points = await fetchPickupPoints(this.cart.external_id);
        this.processPickupPoints(points);
      } catch (error) {
        console.error("Erro ao buscar pontos:", error);
        this.errorMessage = this.$t('shipping.pickup_points_error');
      } finally {
        this.loadingPickupPoints = false;
      }
    },

    // Process pickup points data for hierarchical selection
    processPickupPoints(points) {
      this.allPickupPoints = points;

      // Extrai estados únicos (sem repetição e ordenados)
      this.pickupStates = [...new Set(points.map(p => p.estado))].sort();

      // Reset selections
      this.selectedPickupState = '';
      this.selectedPickupCity = '';
      this.filteredPickupPoints = [];

      // Auto-select if only one state
      if (this.pickupStates.length === 1) {
        this.selectedPickupState = this.pickupStates[0];
        this.updatePickupCities();
      }
    },

    // Update cities based on selected state
    updatePickupCities() {
      this.selectedPickupCity = '';
      this.filteredPickupPoints = [];

      if (!this.selectedPickupState) return;

      // Filtra cidades únicas para o estado selecionado (sem repetição e ordenadas)
      this.pickupCities = [...new Set(
        this.allPickupPoints
          .filter(p => p.estado === this.selectedPickupState)
          .map(p => p.cidade)
      )].sort();

      // Auto-select if only one city
      if (this.pickupCities.length === 1) {
        this.selectedPickupCity = this.pickupCities[0];
        this.updatePickupPoints();
      }
    },

    // Update pickup points based on selected city
    updatePickupPoints() {
      if (!this.selectedPickupState || !this.selectedPickupCity) return;

      this.filteredPickupPoints = this.allPickupPoints.filter(p =>
        p.estado === this.selectedPickupState &&
        p.cidade === this.selectedPickupCity
      );
    },

    isOptionSelected(option) {
      if (this.selectedDeliveryType === 'pickup_point') {
        return false;
      }
      return this.currentSelection.optionId === (option.entrega_servico_id || 'retirada_loja');
    },

    isPickupPointSelected(option, ponto) {
      const pointId = ponto?.id || ponto?.ponto_entrega;
      return this.currentSelection.optionId === (option.entrega_servico_id || 'retirada_loja') &&
        this.currentSelection.pickupPointId === pointId;
    },

    switchToPickupPoints() {
      // If already in pickup_point mode, switch back to normal options
      if (this.selectedDeliveryType === 'pickup_point') {
        this.selectedDeliveryType = null;
        this.currentSelection = {
          optionId: null,
          pickupPointId: null
        };
        return;
      }

      // Otherwise, switch to pickup_point mode
      this.selectedDeliveryType = 'pickup_point';
      this.currentSelection = {
        optionId: null,
        pickupPointId: null
      };

      // If no points loaded, fetch them
      if (this.allPickupPoints.length === 0) {
        this.fetchPickupPoints();
      }
    },

    selectPickupPointFromOption(option, ponto) {
      // Verifica se pontos_entrega é um objeto único e não um array
      if (option.opcionais?.pontos_entrega && !Array.isArray(option.opcionais.pontos_entrega)) {
        // Se for um objeto único, usa esse objeto diretamente
        ponto = option.opcionais.pontos_entrega;
      }

      this.currentSelection = {
        optionId: option.entrega_servico_id || 'retirada_loja',
        pickupPointId: ponto?.id || ponto?.ponto_entrega
      };

      this.$emit('shipping-selected', {
        tipo: option.entrega_servico_id ? 'entrega' : 'retirada',
        valor: option.valor || 0,
        selectedOption: option,
        selectedPickupPoint: ponto
      });

      this.saveShippingData();
    },

    async handlePickupPointSelection(point) {

      this.selectedOptionBeforeLoading = {
        name: point.punto_entrega || point.nome,
        type: 'pickup_point',
        point: point
      };

      this.loading = true;
      try {
        const params = {
          id_ponto_entrega: point.id,
          cidade_alternativa_id: point.cidade_id
        };

        const { shippingCost, deliveryOptions } = await calculateShipping(
          this.cart,
          this.selectedCity,
          this.selectedStateName,
          this.user,
          null,
          params
        );

        this.updateDeliveryOptions(deliveryOptions);
        this.updateShippingCost(shippingCost);
        this.currentSelection.pickupPointId = point.id;

        this.filteredPickupPoints = [];
        this.onlyPickupAvailable = false;
        this.selectedDeliveryType = null;

        this.$emit('shipping-selected', {
          tipo: 'retirada',
          valor: shippingCost,
          selectedOption: null,
          selectedPickupPoint: point
        });

        this.saveShippingData();
      } catch (error) {
        console.error("Erro ao calcular frete para ponto:", error);
        this.errorMessage = this.$t('shipping.pickup_point_error');
      } finally {
        this.loading = false;
      }
    },

    selectPickupPoint(point) {
      if (this.selectedDeliveryType === 'pickup_point') {
        this.handlePickupPointSelection(point);
      } else {
        this.currentSelection.pickupPointId = point.id;
        this.$emit('shipping-selected', {
          tipo: 'retirada',
          valor: 0,
          selectedOption: null,
          selectedPickupPoint: point
        });
        this.saveShippingData();
      }
    },

    selectDeliveryOption(option) {
      this.selectedDeliveryType = option.entrega_servico_id ? 'delivery' : 'pickup';
      this.currentSelection = {
        optionId: option.entrega_servico_id || 'retirada_loja',
        pickupPointId: null
      };

      this.updateShippingCost(option.valor || 0);

      this.$emit('shipping-selected', {
        tipo: option.entrega_servico_id ? 'entrega' : 'retirada',
        valor: option.valor || 0,
        selectedOption: option,
        selectedPickupPoint: null
      });

      this.saveShippingData();
    },

    formatCurrency(value, currency) {
      if (!currency) return `G$ ${value.toLocaleString('pt-BR')}`;
      return `${currency.simbolo} ${value.toLocaleString('pt-BR', {
        minimumFractionDigits: currency.casas_decimais,
        maximumFractionDigits: currency.casas_decimais
      })}`;
    },

    saveShippingData() {
      const data = {
        selectedState: this.selectedState,
        selectedCity: this.selectedCity,
        deliveryOptions: this.deliveryOptions,
        currentSelection: this.currentSelection,
        shippingCost: this.shippingCost,
        selectedDeliveryType: this.selectedDeliveryType,
        selectedPickupState: this.selectedPickupState,
        selectedPickupCity: this.selectedPickupCity,
        cliente_endereco_id: this.clienteEnderecoId
      };
      localStorage.setItem("shippingData", JSON.stringify(data));
    },


    autoFillUserLocation() {
      if (this.user?.enderecos?.length) {
        const endereco = this.user.enderecos[0];
        const paisId = endereco.pais_id;

        this.estados = paisId === 1 ? estados_br : paisId === 2 ? estados_py : [];

        if (endereco.cidade_id) {
          const cidade = this.estados
            .flatMap(e => e.cidades)
            .find(c => c.cidade_id === endereco.cidade_id);

          if (cidade) {
            const estado = this.estados.find(e =>
              e.cidades.some(c => c.cidade_id === cidade.cidade_id)
            );

            if (estado) {
              this.updateSelectedState(estado.estado_id);
              this.updateSelectedCity(cidade.cidade_id);


            }
          }
        }
      } else {
        this.estados = estados_py;
        const defaultEstado = this.estados[0];
        const defaultCidade = defaultEstado.cidades[0];

        this.updateSelectedState(defaultEstado.estado_id);
        this.updateSelectedCity(defaultCidade.cidade_id);
        this.calculateShipping();
      }
    }

  },

  mounted() {
    this.autoFillUserLocation();

    const savedData = JSON.parse(localStorage.getItem("shippingData") || "{}");

    if (savedData.deliveryOptions) {
      this.updateDeliveryOptions(savedData.deliveryOptions);
      this.onlyPickupAvailable = this.isOnlyPickupOption;

      if (savedData.currentSelection) {
        this.currentSelection = savedData.currentSelection;
      }

      if (savedData.selectedDeliveryType) {
        this.selectedDeliveryType = savedData.selectedDeliveryType;
      }

      if (savedData.selectedPickupState) {
        this.selectedPickupState = savedData.selectedPickupState;
      }
      if (savedData.selectedPickupCity) {
        this.selectedPickupCity = savedData.selectedPickupCity;
      }
    }

    if (this.user?.enderecos?.length === 1) {
      const unicoEndereco = this.user.enderecos[0];
      this.clienteEnderecoId = unicoEndereco.id;
      this.selecionarEndereco(unicoEndereco);
    } else if (this.cart?.cliente_endereco_id && this.user?.enderecos?.length) {
      const enderecoSelecionado = this.user.enderecos.find(e => e.id === this.cart.cliente_endereco_id);
      if (enderecoSelecionado) {
        this.clienteEnderecoId = enderecoSelecionado.id;
        this.selecionarEndereco(enderecoSelecionado);
      }
    }
  },



  watch: {
    'user.enderecos': {
      handler(novosEnderecos) {
        if (this.cart?.cliente_endereco_id && novosEnderecos?.length) {
          const enderecoSelecionado = novosEnderecos.find(e => e.id === this.cart.cliente_endereco_id);
          if (enderecoSelecionado) {
            this.clienteEnderecoId = enderecoSelecionado.id;
            this.selecionarEndereco(enderecoSelecionado);
          }
        }
      },
      immediate: true
    },

    deliveryOptions() { this.saveShippingData(); },
    shippingCost() { this.saveShippingData(); },
    currentSelection: {
      deep: true,
      handler() { this.saveShippingData(); }
    },
    selectedDeliveryType() { this.saveShippingData(); },
    selectedPickupState() { this.saveShippingData(); },
    selectedPickupCity() { this.saveShippingData(); }
  }

};
</script>

<style scoped>
.shipping-modern {
  --primary-color: #E23B33;
  --primary-hover: #B58C4F;
  --success-color: #10b981;
  --warning-color: #f59e0b;
  --error-color: #ef4444;
  --text-color: #374151;
  --text-light: #6b7280;
  --border-color: #e5e7eb;
  --bg-hover: #f9fafb;
  --bg-selected: #ffffff;
  --radius-sm: 6px;
  --radius-md: 8px;
  --radius-lg: 12px;
  --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  --transition: all 0.2s ease;
}

.shipping-card {
  background: white;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  padding: 24px;
  position: relative;
  overflow: hidden;
}

.shipping-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.shipping-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: var(--radius-lg);
}

.shipping-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(79, 70, 229, 0.1);
  border-radius: 50%;
  border-top-color: var(--primary-color);
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.shipping-content.is-loading {
  opacity: 0.6;
  pointer-events: none;
}

.shipping-location {
  margin-bottom: 24px;
}

.shipping-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.shipping-field {
  margin-bottom: 0;
}

.shipping-label {
  display: block;
  margin-bottom: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-color);
}

.select-wrapper {
  position: relative;
}

.shipping-select {
  width: 100%;
  padding: 10px 36px 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  font-size: 0.9375rem;
  color: var(--text-color);
  appearance: none;
  background-color: white;
  transition: var(--transition);
}

.shipping-select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.select-arrow {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  pointer-events: none;
  color: var(--text-light);
}

.select-arrow::after {
  content: "";
  position: absolute;
  width: 8px;
  height: 8px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(45deg);
}

.shipping-calculate-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-size: 0.9375rem;
  font-weight: 500;
  transition: var(--transition);
  width: 100%;
}

.shipping-calculate-btn:hover:not(:disabled) {
  background: var(--primary-hover);
  transform: translateY(-1px);
}

.shipping-calculate-btn:disabled {
  background: #d1d5db;
  cursor: not-allowed;
  opacity: 0.7;
}

.shipping-calculate-btn svg {
  transition: var(--transition);
}

.shipping-calculate-btn:hover:not(:disabled) svg {
  transform: translateX(2px);
}

.shipping-user-address {
  margin-bottom: 24px;
}

.shipping-address-selection {
  margin-bottom: 32px;
}

.shipping-address-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.shipping-location-header {
  margin-bottom: 24px;
  padding: 12px 16px;
  border-left: 4px solid var(--primary-color);
  background-color: #f0fdf4;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  gap: 10px;
  color: #166534;
}

.shipping-location-header svg {
  color: #22c55e;
}

.shipping-location-header strong {
  font-weight: 600;
}

.address-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #f0fdf4;
  border-radius: var(--radius-sm);
  color: #166534;
  font-size: 0.875rem;
}

.address-badge svg {
  color: #22c55e;
}

.address-badge strong {
  font-weight: 500;
}

.shipping-notice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  background: #fffbeb;
  border-radius: var(--radius-sm);
  margin-bottom: 24px;
  color: #92400e;
}

.notice-icon {
  flex-shrink: 0;
  color: #f59e0b;
}

.shipping-notice p {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
}

.shipping-options {
  margin-top: 24px;
}

.shipping-subtitle {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.shipping-subtitle svg {
  color: var(--primary-color);
}

.shipping-options-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.shipping-option {
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  padding: 16px;
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  gap: 12px;
}

.shipping-option:hover {
  border-color: var(--primary-color);
  background: var(--bg-hover);
}

.shipping-option.is-selected {
  border-color: var(--primary-color);
  background: var(--bg-selected);
  box-shadow: 0 0 0 1px var(--primary-color);
}

.shipping-option-radio {
  display: flex;
  align-items: flex-start;
  padding-top: 2px;
}

.shipping-radio-input {
  opacity: 0;
  position: absolute;
}

.shipping-radio-control {
  width: 18px;
  height: 18px;
  border: 2px solid #d1d5db;
  border-radius: 50%;
  position: relative;
  transition: var(--transition);
  flex-shrink: 0;
}

.shipping-radio-input:checked+.shipping-radio-control {
  border-color: var(--primary-color);
  background: var(--primary-color);
}

.shipping-radio-input:checked+.shipping-radio-control::after {
  content: '';
  position: absolute;
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
  top: 3px;
  left: 3px;
}

.shipping-option-label {
  display: flex;
  flex-direction: column;
  gap: 8px;
  cursor: pointer;
  flex-grow: 1;
}

.show-delivery-btn {
  margin: 1rem 0;
  padding: 0.5rem 1rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

.show-delivery-btn:hover {
  background-color: #0056b3;
}

.shipping-option-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.option-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
}

.shipping-option-name {
  font-weight: 500;
  color: var(--text-color);
}

.shipping-option-time {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--text-light);
  font-size: 0.8125rem;
}

.shipping-option-time svg {
  color: var(--text-light);
}

.shipping-option-price {
  font-weight: 600;
  color: var(--text-color);
  white-space: nowrap;
  margin-left: 12px;
}

.shipping-option-free {
  color: var(--success-color);
  font-weight: 600;
  white-space: nowrap;
  margin-left: 12px;
}

.shipping-sub-options {
  margin-top: 12px;
  padding-left: 24px;
  border-left: 2px solid #e5e7eb;
}

.shipping-sub-option {
  padding: 12px;
  margin: 8px 0;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  gap: 12px;
}

.shipping-sub-option:hover {
  background-color: var(--bg-hover);
}

.shipping-sub-option.is-selected {
  background-color: var(--bg-selected);
  border-left: 3px solid var(--primary-color);
}

.shipping-sub-option-radio {
  display: flex;
  align-items: flex-start;
  padding-top: 2px;
}

.shipping-sub-option-label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  cursor: pointer;
  flex-grow: 1;
}

.shipping-sub-option-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.shipping-sub-option-name {
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--text-color);
}

.shipping-selected-loading {
  margin-bottom: 1rem;
  text-align: center;
}

.selected-option-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: rgba(var(--primary-color-rgb), 0.1);
  border-radius: 0.5rem;
  margin-bottom: 0.5rem;
}

.selected-option-badge svg {
  color: var(--success-color);
}

.shipping-loading-text {
  font-size: 0.9rem;
  color: var(--text-muted);
}

.shipping-sub-option-address,
.shipping-sub-option-hours {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8125rem;
  color: var(--text-light);
}

.shipping-sub-option-address svg,
.shipping-sub-option-hours svg {
  color: var(--text-light);
  flex-shrink: 0;
}

.shipping-pickup {
  margin-top: 16px;
}

.shipping-pickup-option {
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  padding: 16px;
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  gap: 12px;
}

.shipping-pickup-option:hover {
  border-color: var(--primary-color);
}

.shipping-pickup-option.is-selected {
  border-color: var(--primary-color);
  background: var(--bg-selected);
}

.shipping-pickup-points {
  margin-top: 12px;
}

.shipping-pickup-points-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

.shipping-pickup-point {
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  padding: 16px;
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  gap: 12px;
}

.shipping-pickup-point:hover {
  border-color: var(--primary-color);
  background: var(--bg-hover);
}

.shipping-pickup-point.is-selected {
  border-color: var(--primary-color);
  background: var(--bg-selected);
}

.shipping-pickup-point-radio {
  display: flex;
  align-items: flex-start;
  padding-top: 2px;
}

.shipping-pickup-point-label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  cursor: pointer;
  flex-grow: 1;
}

.shipping-pickup-point-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.shipping-pickup-point-name {
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--text-color);
}

.address-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.address-item {
  background: #fdfaf5;
  border: 1px solid #e4d5c3;
  border-radius: 0.75rem;
  padding: 1rem;
  transition: all 0.2s ease;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  position: relative;
}

.address-item:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
  border-color: #E23B33;
}

.address-item.selected {
  border: 2px solid #E23B33;
  background: #f8f0e5;
  box-shadow: 0 0 0 2px #E23B3333;
}

.radio-hidden {
  display: none;
}

.address-content {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.875rem;
}

.btn-primary {
  background: #E23B33;
  color: white;
  border: none;
  border-radius: 5px;
  padding: 12px 20px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.btn-primary:hover {
  background: #E23B33;
}

.btn-primary:disabled {
  background: #b0c4de;
}

.address-content strong {
  font-weight: 600;
  font-size: 1rem;
  color: #3c2f21;
}

.address-content small {
  color: #6b4e31;
  line-height: 1.3;
}

.badge-main {
  display: inline-block;
  background: #E23B33;
  color: white;
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 0.5rem;
  margin-top: 0.5rem;
  font-weight: 500;
}

.shipping-pickup-point-address,
.shipping-pickup-point-hours {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8125rem;
  color: var(--text-light);
}

.shipping-pickup-point-address svg,
.shipping-pickup-point-hours svg {
  color: var(--text-light);
  flex-shrink: 0;
}

.endereco-card {
  border: 1px solid #ccc;
  padding: 1rem;
  margin-bottom: 1rem;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s ease;
}

.endereco-card:hover {
  background-color: #f5f5f5;
}

.endereco-card.selected {
  border-color: #2ecc71;
  background-color: #eafaf1;
}

.principal {
  font-size: 0.9rem;
  color: #f39c12;
}

.shipping-no-results {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: #f8fafc;
  border-radius: var(--radius-sm);
  color: var(--text-light);
  margin-top: 16px;
}

.shipping-no-results svg {
  color: var(--text-light);
}

.shipping-no-results p {
  margin: 0;
  font-size: 0.875rem;
}

.shipping-error {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 12px;
  background: #fef2f2;
  border-radius: var(--radius-sm);
  color: var(--error-color);
  font-size: 0.875rem;
}

.shipping-error svg {
  flex-shrink: 0;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (max-width: 640px) {
  .shipping-grid {
    grid-template-columns: 1fr;
  }

  .shipping-card {
    padding: 16px;
  }

  .shipping-title {
    font-size: 1.125rem;
  }
}
</style>